import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Clock3, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { getMyServiceOrder } from '@/lib/api';
import { apiErrorMessage, orderNumber, unwrapEntity } from '@/lib/documentServices';
import { ServiceError, ServiceLoading, ServicePageHeader } from '@/components/document-services/DocumentServicesCommon';

export default function ServicePaymentResult() {
  const { orderId } = useParams();
  const location = useLocation();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    setLoading(true); setError('');
    try { const { data } = await getMyServiceOrder(orderId); setOrder(unwrapEntity(data, ['order'])); }
    catch (err) { setError(apiErrorMessage(err, 'Payment status could not be loaded.')); }
    finally { setLoading(false); }
  }, [orderId]);
  useEffect(() => { load(); }, [load]);
  if (loading) return <ServiceLoading label="Confirming payment status…" />;
  if (error || !order) return <ServiceError message={error || 'Order not found.'} onRetry={load} />;

  const verified = location.state?.paymentResult?.verified === true || order.payment_status === 'paid';
  const failed = ['failed', 'cancelled'].includes(order.payment_status);
  const review = ['processing', 'review_required', 'pending'].includes(order.payment_status);
  const Icon = verified ? CheckCircle2 : failed ? XCircle : review ? Clock3 : AlertTriangle;
  const tone = verified ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : failed ? 'border-red-200 bg-red-50 text-red-800' : 'border-amber-200 bg-amber-50 text-amber-900';
  const title = verified ? 'Payment verified' : failed ? 'Payment not completed' : 'Verification in progress';
  const copy = verified
    ? 'MCC verified the Moneris receipt and your document order can now enter fulfillment.'
    : failed
      ? 'Your order remains unpaid and saved. If the order allows another attempt, start it from the order detail page.'
      : 'MCC is reconciling the provider response. Do not make another payment until the order status is confirmed.';
  return (
    <div className="mx-auto max-w-3xl space-y-6 animate-fade-in">
      <ServicePageHeader eyebrow={orderNumber(order)} title="Payment result" description="The status below comes from MCC's server-side Moneris verification, not from a browser callback alone." />
      <Card className={`border shadow-sm ${tone}`}><CardContent className="p-8 text-center"><Icon className="mx-auto size-12" /><h2 className="mt-4 text-2xl font-semibold">{title}</h2><p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed opacity-85">{copy}</p><div className="mt-6 flex flex-col justify-center gap-2 sm:flex-row"><Button asChild className="bg-[#062B1A] text-white"><Link to={`/student/orders/${encodeURIComponent(orderId)}`}>View order</Link></Button><Button asChild variant="outline"><Link to="/student/orders">All orders</Link></Button></div></CardContent></Card>
    </div>
  );
}
